import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useToken } from '../contexts/TokenContext';
import { tradingAgentApi } from '../services/api';
import { walletApi } from '../services/walletApi';
import Sidebar from '../components/Sidebar';
import AgentChat from '../components/AgentChat';
import AgentTerminal from '../components/AgentTerminal';
import Navbar from '../components/Navbar';
import TerminalControls from '../components/TerminalControls';
import WalletConnectSection from '../components/WalletConnectSection';
import { formatAgentName } from '../utils/formatNames';
import globalWebSocketService from '../services/globalWebSocketService';

const Dashboard = () => {
  const { currentUser } = useAuth();
  const { tokenId } = useToken();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [walletConnected, setWalletConnected] = useState(false);
  const [walletBalance, setWalletBalance] = useState(null);
  const [terminalVisible, setTerminalVisible] = useState(false);
  const [terminalCollapsed, setTerminalCollapsed] = useState(false);
  const [terminalOutput, setTerminalOutput] = useState([]);
  const [currentStrategy, setCurrentStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaused, setIsPaused] = useState(false);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [wallets, setWallets] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const chatEndRef = useRef(null);
  const agentChatRef = useRef(null);
  const [connectionLock, setConnectionLock] = useState(false);
  const [walletAddresses, setWalletAddresses] = useState(false);
  const [autoConnectEnabled, setAutoConnectEnabled] = useState(false);
  const [copiedAgentId, setCopiedAgentId] = useState(false);
  
  // Fetch wallet balance if connected
const fetchWalletBalance = useCallback(async (walletAddress) => {
  // Add a timestamp to prevent duplicate calls within a short timeframe
  const now = Date.now();
  const lastFetchTime = fetchWalletBalance.lastFetch || 0;
  
  // Only fetch if more than 3 seconds have passed since last fetch
  if (now - lastFetchTime < 3000) {
    console.log('Skipping duplicate wallet balance fetch');
    return;
  }
  
  fetchWalletBalance.lastFetch = now;
  
  try {
    const address = walletAddress || (selectedWallet?.address);
    if (!address) {
      console.warn('No wallet address provided for balance fetch');
      return;
    }

    console.log(`Dashboard: Fetching balance for wallet: ${address}`);
    const balance = await tradingAgentApi.getWalletBalance(address);
    console.log('Dashboard: Received wallet balance:', balance);
    
    // Log the specific SOL balance value
    console.log('Dashboard: SOL balance value:', balance.solBalance);
    console.log('Dashboard: SOL balance type:', typeof balance.solBalance);
    
    // Double-check the value before setting state
    setWalletBalance(prev => {
      console.log('Dashboard: Previous balance state:', prev);
      console.log('Dashboard: Setting new balance state:', balance);
      return balance;
    });
  } catch (error) {
    console.error('Dashboard: Failed to fetch wallet balance:', error);
  }
}, [selectedWallet]);
  // Make sure we initialize this property
  fetchWalletBalance.lastFetch = 0;

  // Optimized connectWallet function
  const connectWallet = useCallback(async (walletToConnect = null) => {
    try {
      // Prevent duplicate connection attempts
      if (connecting) {
        console.log('Connection already in progress, ignoring duplicate request');
        return false;
      }
      
      setConnecting(true);
      setConnectionLock(true);
      
      // Use the provided wallet or the currently selected wallet
      const wallet = walletToConnect || selectedWallet;
      
      if (!wallet || !wallet.address) {
        // Check if user has any wallets at all
        if (wallets.length === 0) {
          setError('No wallets found. Please create a wallet first by going to the Wallet Groups page.');
        } else {
          setError('Please select a wallet first.');
        }
        return false;
      }
      
      // Check if already connected to this wallet
      if (walletConnected && selectedWallet?.address === wallet.address) {
        console.log(`Already connected to wallet ${wallet.address}, refreshing balance only`);
        await fetchWalletBalance(wallet.address);
        return true;
      }
      
      console.log('Connecting wallet:', wallet.address);
      
      try {
        // Call the API to connect the wallet
        const result = await tradingAgentApi.connectWallet(wallet.address);
        
        // Only update state if connection was successful
        if (result.success) {
          setWalletConnected(true);
          
          // Fetch wallet balance immediately
          await fetchWalletBalance(wallet.address);
          
          return true;
        } else {
          console.error('Wallet connection returned false:', result);
          setError('Failed to connect wallet: ' + (result.message || 'Unknown error'));
          return false;
        }
      } catch (apiError) {
        // If API call fails, still allow local connection for UI purposes
        console.warn('API connection failed, but allowing local connection:', apiError.message);
        setWalletConnected(true);
        
        // Fetch wallet balance immediately
        await fetchWalletBalance(wallet.address);
        
        return true;
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      // Only show error if user explicitly tried to connect (not auto-connect)
      if (walletToConnect) {
        setError('Failed to connect wallet. Please try again.');
      }
      return false;
    } finally {
      setConnecting(false);
      // Release the connection lock after a short delay
      setTimeout(() => {
        setConnectionLock(false);
      }, 1000);
    }
  }, [connecting, selectedWallet, walletConnected, fetchWalletBalance]);

  useEffect(() => {
    if (!selectedAgent) return;
    
    // Just load the saved wallet for this agent, but don't connect automatically
    const loadSavedWallet = async () => {
      try {
        // Check if there's a selected wallet for this agent
        const strategyDetailsKey = `agent_strategy_${selectedAgent.id}`;
        const storedDetails = localStorage.getItem(strategyDetailsKey);
        
        if (storedDetails) {
          try {
            const parsedDetails = JSON.parse(storedDetails);
            if (parsedDetails.walletAddresses && parsedDetails.walletAddresses.length > 0) {
              const savedAddress = parsedDetails.walletAddresses[0];
              // Find the matching wallet
              const matchingWallet = wallets.find(w => w.address === savedAddress);
              if (matchingWallet) {
                console.log(`Found saved wallet ${matchingWallet.address} for agent ${selectedAgent.id}`);
                // Only update if different from current selection to prevent loops
                if (!selectedWallet || selectedWallet.address !== matchingWallet.address) {
                  // Enable auto-connect when loading saved wallet for existing agents
                  setAutoConnectEnabled(true);
                  setSelectedWallet(matchingWallet);
                  setWalletAddresses([matchingWallet.address]);
                }
              }
            }
          } catch (e) {
            console.error('Error parsing saved agent strategy details:', e);
          }
        } else if (wallets.length > 0 && !selectedWallet) {
          // If no saved wallet but we have wallets, select default or first
          const defaultWallet = wallets.find(w => w.is_default) || wallets[0];
          if (defaultWallet) {
            console.log(`Using default wallet ${defaultWallet.address} for agent ${selectedAgent.id}`);
            // Enable auto-connect when loading default wallet
            setAutoConnectEnabled(true);
            setSelectedWallet(defaultWallet);
            setWalletAddresses([defaultWallet.address]);
          }
        }
      } catch (error) {
        console.error('Error loading saved wallet:', error);
      }
    };
    
    loadSavedWallet();
  }, [selectedAgent, wallets, selectedWallet]);

  useEffect(() => {
    let balanceInterval = null;
    
    // Only set up the interval if a wallet is connected and not in the middle of connecting
    if (walletConnected && selectedWallet?.address && !connectionLock && !connecting) {
      console.log(`Setting up balance polling for wallet ${selectedWallet.address}`);
      
      // Initial fetch, but don't await it
      fetchWalletBalance(selectedWallet.address).catch(error => {
        console.error('Error fetching initial balance:', error);
      });
      
      // Set up interval with a reasonable delay (30 seconds)
      balanceInterval = setInterval(() => {
        if (!connectionLock && !connecting) {
          fetchWalletBalance(selectedWallet.address).catch(error => {
            console.error('Error fetching balance in interval:', error);
          });
        }
      }, 30000); // 30 seconds interval - much longer to reduce load
    }
    
    // Cleanup function
    return () => {
      if (balanceInterval) {
        console.log('Clearing balance polling interval');
        clearInterval(balanceInterval);
      }
    };
  }, [walletConnected, selectedWallet, fetchWalletBalance, connectionLock, connecting]);

  useEffect(() => {
    return () => {
      // Cleanup function
      if (walletConnected) {
        console.log('Dashboard unmounting, disconnecting wallet');
        try {
          tradingAgentApi.disconnectWallet();
        } catch (error) {
          console.error('Error disconnecting wallet during cleanup:', error);
        }
      }
    };
  }, [walletConnected]);

  useEffect(() => {
    // Only attempt auto-connect when explicitly enabled and we have a wallet selected
    if (autoConnectEnabled && selectedWallet && !walletConnected && !connecting && !connectionLock) {
      // Auto-connect with a delay to prevent loops
      const connectTimer = setTimeout(() => {
        console.log('Auto-connecting newly selected wallet:', selectedWallet.address);
        connectWallet(selectedWallet).catch(error => {
          console.error('Error auto-connecting wallet:', error);
        });
        // Disable auto-connect after use
        setAutoConnectEnabled(false);
      }, 500);
      
      return () => clearTimeout(connectTimer);
    }
  }, [autoConnectEnabled, selectedWallet, walletConnected, connecting, connectionLock, connectWallet]);

  // Fetch agents on component mount
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoading(true);
        const agentList = await tradingAgentApi.getAgents(tokenId);
        console.log('Fetched agents for token:', tokenId, agentList);
        
        // Cache agents for offline use
        tradingAgentApi.cacheAgents(agentList);
        
        setAgents(agentList);
        
        // If agents exist, select the first one
        if (agentList.length > 0) {
          console.log('Auto-selecting first agent on page load:', agentList[0]);
          selectAgent(agentList[0].id); // Use selectAgent function instead of setSelectedAgent
        }
        
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch agents:', error);
        setError('Failed to load agents. Please try again.');
        setLoading(false);
      }
    };

    if (tokenId) {
      fetchAgents();
    }
  }, [tokenId]);

  // Fetch wallets on component mount
  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const response = await walletApi.getUserWallets(null, tokenId);
        console.log('Fetched wallets for token:', tokenId, response);
        
        // Ensure we have a wallets array
        if (!response || !response.wallets) {
          console.error('Invalid response format from getUserWallets');
          return;
        }
        
        setWallets(response.wallets);
        
        // If a default wallet exists, set it as selected
        const defaultWallet = response.wallets.find(w => w.is_default);
        if (defaultWallet) {
          setSelectedWallet(defaultWallet);
        } else if (response.wallets.length > 0) {
          // Otherwise, use the first wallet
          setSelectedWallet(response.wallets[0]);
        }
      } catch (error) {
        console.error('Failed to fetch wallets:', error);
      }
    };
    
    if (tokenId) {
      fetchWallets();
    }
  }, [tokenId]);

  // Listen for auto-stop events to refresh UI state
  useEffect(() => {
    const handleAutoStop = (event) => {
      const { agentId, strategyId } = event.detail;
      console.log(`[Dashboard] Received auto-stop event for agent ${agentId}, strategy ${strategyId}`);
      
      // Refresh agent data to get updated status
      if (selectedAgent && selectedAgent.id === parseInt(agentId)) {
        console.log(`[Dashboard] Refreshing selected agent ${agentId} after auto-stop`);
        
        // Refresh the selected agent details
        tradingAgentApi.getAgentById(agentId).then(agentDetails => {
          console.log(`[Dashboard] Updated agent details after auto-stop:`, agentDetails);
          
          // Update the agent in our agents list
          setAgents(prevAgents => prevAgents.map(agent => 
            agent.id === parseInt(agentId) 
              ? { ...agent, active: agentDetails.active }
              : agent
          ));
          
          // Update selected agent
          setSelectedAgent(prevAgent => ({ 
            ...prevAgent, 
            active: agentDetails.active 
          }));
          
          // Update current strategy status
          setCurrentStrategy(prevStrategy => 
            prevStrategy ? { ...prevStrategy, status: 'stopped' } : null
          );
          
          // Reset action states
          setActionInProgress(false);
          setIsPaused(false);
          
        }).catch(error => {
          console.error(`[Dashboard] Failed to refresh agent ${agentId} after auto-stop:`, error);
        });
      }
    };
    
    // Add event listener
    window.addEventListener('strategy-auto-stopped', handleAutoStop);
    
    // Cleanup
    return () => {
      window.removeEventListener('strategy-auto-stopped', handleAutoStop);
    };
  }, [selectedAgent]);

  useEffect(() => {
    // Check if there's a wallet already connected in localStorage
    const connectedAddress = tradingAgentApi.getConnectedWalletAddress();
    
    if (connectedAddress && wallets.length > 0 && !walletConnected && !connectionLock) {
      // Find the matching wallet
      const matchingWallet = wallets.find(w => w.address === connectedAddress);
      
      if (matchingWallet) {
        console.log(`Found previously connected wallet ${matchingWallet.address}, selecting it`);
        // Only update if it's different from the current selection
        if (!selectedWallet || selectedWallet.address !== matchingWallet.address) {
          setSelectedWallet(matchingWallet);
        }
        
        // Set connected state directly instead of reconnecting
        setWalletConnected(true);
        
        // Fetch balance without auto-connecting
        fetchWalletBalance(matchingWallet.address).catch(error => {
          console.error('Error fetching initial balance for reconnected wallet:', error);
        });
      }
    }
  }, [wallets, walletConnected, connectionLock, fetchWalletBalance, selectedWallet]);



const addTerminalOutput = async (message) => {
  // First update the UI with the new message
  setTerminalOutput(prev => [...prev, message]);
  
  // Then save to database if we have an agent
  if (selectedAgent && selectedAgent.id) {
    try {
      // Extract the actual message without timestamp if it has one
      const messageContent = typeof message === 'string' 
        ? message.replace(/^\[\d{1,2}:\d{1,2}:\d{1,2}( [AP]M)?\] /, '') 
        : message;
      
      // Save to database
      await tradingAgentApi.addTerminalLog(
        selectedAgent.id,
        messageContent,
        currentStrategy,
        selectedAgent.external_id
      );
      
      console.log(`Saved terminal message to database for agent ${selectedAgent.id}`);
    } catch (error) {
      console.error('Error saving terminal output to database:', error);
    }
  }
};
  // Handle wallet selection from agent chat
  const handleWalletSelectFromChat = (wallet) => {
    if (!wallet || !wallet.address) return;
    
    // Only update if it's a different wallet to prevent loops
    if (!selectedWallet || selectedWallet.address !== wallet.address) {
      // Just update the selected wallet but DON'T automatically connect
      setSelectedWallet(wallet);
      
      // Update the wallet addresses state
      setWalletAddresses([wallet.address]);
      
      // Save the selection to localStorage for the current agent
      if (selectedAgent && selectedAgent.id) {
        try {
          const strategyDetailsKey = `agent_strategy_${selectedAgent.id}`;
          const storedDetails = localStorage.getItem(strategyDetailsKey) || '{}';
          const parsedDetails = JSON.parse(storedDetails);
          parsedDetails.walletAddresses = [wallet.address];
          localStorage.setItem(strategyDetailsKey, JSON.stringify(parsedDetails));
        } catch (error) {
          console.error('Error saving wallet address:', error);
        }
      }
      
      // Enable auto-connect since this was a user selection
      setAutoConnectEnabled(true);
      
      console.log(`Wallet ${wallet.address} selected and auto-connect enabled`);
    } else {
      console.log(`Wallet ${wallet.address} already selected, ignoring duplicate selection`);
    }
  };

  const createAgent = async () => {
    try {
      // Clear any existing errors
      setError(null);
      
      // Generate a unique name that doesn't conflict with existing agents
      let newAgentNumber = 1;
      const existingNumbers = new Set();
      
      // Extract agent numbers from existing agent names
      agents.forEach(agent => {
        const match = agent.name.match(/Agent\s+(\d+)/i);
        if (match && match[1]) {
          existingNumbers.add(parseInt(match[1], 10));
        }
      });
      
      // Find the first available number that's not in use
      while (existingNumbers.has(newAgentNumber)) {
        newAgentNumber++;
      }
      
      // Create the new agent name with the available number
      const agentName = `Agent ${newAgentNumber}`;
      
      console.log(`Creating new agent with unique name: ${agentName}, tokenId: ${tokenId}`);
      const newAgent = await tradingAgentApi.createAgent(agentName, tokenId);
      console.log('Created new agent:', newAgent);
      setAgents(prev => [...prev, newAgent]);
      setSelectedAgent(newAgent);
      
      // Reset terminal and wallet connection state
      setTerminalVisible(false);
      setTerminalOutput([]);
      setCurrentStrategy(null);
      setIsPaused(false);
      setWalletConnected(false);
      setWalletBalance(null);
      setAutoConnectEnabled(false);
    } catch (error) {
      console.error('Failed to create agent:', error);
      setError('Failed to create agent. Please try again.');
    }
  };

  const copyAgentId = () => {
    if (selectedAgent && selectedAgent.external_id) {
      navigator.clipboard.writeText(selectedAgent.external_id).then(() => {
        setCopiedAgentId(true);
        setTimeout(() => setCopiedAgentId(false), 2000);
      }).catch(err => {
        console.error('Failed to copy agent ID:', err);
      });
    }
  };

const deleteAgent = async () => {
  if (!selectedAgent) return;

  try {
    setActionInProgress(true);
    console.log('Deleting agent:', selectedAgent.id, 'with external_id:', selectedAgent.external_id);
    
    // Call the backend to delete the agent using the endpoint
    // Make sure we're passing both the internal ID and external_id
    const result = await tradingAgentApi.deleteAgent(selectedAgent.id, selectedAgent.external_id);
    console.log('Delete agent response:', result);
    
    // Remove the agent from the local state
    setAgents(prev => prev.filter(agent => agent.id !== selectedAgent.id));
    
    // If this was the selected agent, clear it
    if (selectedAgent) {
      setSelectedAgent(null);
      setTerminalVisible(false);
      setCurrentStrategy(null);
    }
    
    // Close the confirmation dialog
    setDeleteConfirmOpen(false);
    
    // If there are other agents, select the first one
    if (agents.length > 1) {
      const remainingAgents = agents.filter(agent => agent.id !== selectedAgent.id);
      if (remainingAgents.length > 0) {
        setSelectedAgent(remainingAgents[0]);
      }
    }
  } catch (error) {
    console.error('Failed to delete agent:', error);
    setError('Failed to delete agent. Please try again.');
  } finally {
    setActionInProgress(false);
  }
};
  
  const selectAgent = async (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    
    if (!agent) {
      console.error('Agent not found:', agentId);
      return;
    }
    
    // Clear any existing errors when switching agents
    setError(null);
    
    // Reset wallet connection state when switching agents
    setWalletConnected(false);
    setWalletBalance(null);
    
    // Disable auto-connect initially when switching agents
    setAutoConnectEnabled(false);
    
    // Set the selected agent
    setSelectedAgent(agent);
    
    // Reset terminal output when switching agents
    setTerminalOutput([]);
    
    // Check localStorage for agent status and active strategy
    const cachedStatus = localStorage.getItem(`agent_status_${agentId}`);
    let cachedStrategy = localStorage.getItem(`active_strategy_${agentId}`);
    let isAgentActive = false;
    let shouldShowTerminal = false;
    
    if (cachedStatus) {
      try {
        const parsedStatus = JSON.parse(cachedStatus);
        isAgentActive = parsedStatus.active;
        
        // If we have a cached status that says the agent is active, we should show the terminal
        if (isAgentActive) {
          shouldShowTerminal = true;
          
          // If we have a cached strategy ID, use it
          if (parsedStatus.strategyId) {
            cachedStrategy = parsedStatus.strategyId;
            setCurrentStrategy(parsedStatus.strategyId);
          }
          
          // Check if the strategy is paused
          if (parsedStatus.isPaused) {
            setIsPaused(true);
          } else {
            setIsPaused(false);
          }
        }
      } catch (e) {
        console.error('Error parsing cached agent status:', e);
      }
    }
    
    try {
      // Fetch agent details including active strategies
      const agentDetails = await tradingAgentApi.getAgentById(agentId);
      console.log('Agent details:', agentDetails);
      
      // Update agent with additional details
      setSelectedAgent(agentDetails);
      
      // Check if agent is active and should show terminal
      const activeStrategy = agentDetails.strategies?.find(s => s.status === 'active');
      const pausedStrategy = agentDetails.strategies?.find(s => s.status === 'paused');
      
      // If the agent has an active or paused strategy, or was active according to localStorage
      shouldShowTerminal = shouldShowTerminal || !!activeStrategy || !!pausedStrategy || agentDetails.active;
      
      // Check if agent has existing terminal logs - if so, show terminal
      if (!shouldShowTerminal) {
        try {
          const terminalLogs = await tradingAgentApi.getTerminalLogs(agentId);
          if (terminalLogs && terminalLogs.length > 0) {
            console.log(`Found ${terminalLogs.length} terminal logs for agent ${agentId}, showing terminal`);
            shouldShowTerminal = true;
          }
        } catch (error) {
          console.error('Error checking for terminal logs:', error);
        }
      }
      
      // If we should show the terminal
      if (shouldShowTerminal) {
        setTerminalVisible(true);
        
        // If we have an active strategy from the API, use it
        if (activeStrategy) {
          setCurrentStrategy(activeStrategy.id);
          setIsPaused(false);
          
          // Update localStorage with the strategy ID
          localStorage.setItem(`active_strategy_${agentId}`, activeStrategy.id);
          localStorage.setItem(`agent_status_${agentId}`, JSON.stringify({
            active: true,
            strategyId: activeStrategy.id,
            externalId: agentDetails.external_id
          }));
          
          // Start persistent WebSocket connection for active strategy - LOGS type
          if (agentDetails.external_id) {
            console.log(`[Dashboard] Starting persistent WebSocket for LOGS for existing active strategy ${activeStrategy.id}`);
            // Use 'logs' connection type for terminal logs
            globalWebSocketService.startConnection(agentId, agentDetails.external_id, activeStrategy.id, 'logs');
          }
          
          // Fetch logs for the active strategy
          try {
            const logs = await tradingAgentApi.getStrategyLogs(agentId, activeStrategy.id);
            if (logs && logs.length > 0) {
              // Format logs for display
              const formattedLogs = logs.map(log => {
                const timestamp = new Date(log.timestamp).toLocaleTimeString();
                return `[${timestamp}] ${log.message}`;
              });
              setTerminalOutput(formattedLogs);
            }
          } catch (error) {
            console.error('Failed to fetch strategy logs:', error);
          }
        } 
        // If we have a paused strategy from the API, use it
        else if (pausedStrategy) {
          setCurrentStrategy(pausedStrategy.id);
          setIsPaused(true);
          
          // Update localStorage with the strategy ID
          localStorage.setItem(`active_strategy_${agentId}`, pausedStrategy.id);
          localStorage.setItem(`agent_status_${agentId}`, JSON.stringify({
            active: true,
            strategyId: pausedStrategy.id,
            externalId: agentDetails.external_id,
            isPaused: true
          }));
          
          // Fetch logs for paused strategy
          try {
            const logs = await tradingAgentApi.getStrategyLogs(agentId, pausedStrategy.id);
            if (logs && logs.length > 0) {
              // Format logs for display
              const formattedLogs = logs.map(log => {
                const timestamp = new Date(log.timestamp).toLocaleTimeString();
                return `[${timestamp}] ${log.message}`;
              });
              setTerminalOutput(formattedLogs);
            }
          } catch (error) {
            console.error('Failed to fetch strategy logs:', error);
          }
        }
        // If we don't have a strategy from the API but the agent is active, try using cached strategy
        else if (agentDetails.active && cachedStrategy) {
          setCurrentStrategy(cachedStrategy);
          
          // Fetch logs for the cached strategy
          try {
            const logs = await tradingAgentApi.getStrategyLogs(agentId, cachedStrategy);
            if (logs && logs.length > 0) {
              // Format logs for display
              const formattedLogs = logs.map(log => {
                const timestamp = new Date(log.timestamp).toLocaleTimeString();
                return `[${timestamp}] ${log.message}`;
              });
              setTerminalOutput(formattedLogs);
            }
          } catch (error) {
            console.error('Failed to fetch strategy logs for cached strategy:', error);
          }
        }
      } else {
        // Even if agent appears inactive, check for terminal logs one more time
        try {
          const terminalLogs = await tradingAgentApi.getTerminalLogs(agentId);
          if (terminalLogs && terminalLogs.length > 0) {
            console.log(`[Dashboard] Agent appears inactive but has ${terminalLogs.length} logs, showing terminal anyway`);
            setTerminalVisible(true);
            // Don't set strategy since there's no active one, but show the terminal
          } else {
            console.log(`[Dashboard] Agent is inactive and has no logs, hiding terminal`);
            setTerminalVisible(false);
            setCurrentStrategy(null);
            setIsPaused(false);
          }
        } catch (error) {
          console.error('Error checking terminal logs for inactive agent:', error);
          setTerminalVisible(false);
          setCurrentStrategy(null);
          setIsPaused(false);
        }
      }
      
      // Check if there's a saved wallet for this agent
      const strategyDetailsKey = `agent_strategy_${agentId}`;
      const storedDetails = localStorage.getItem(strategyDetailsKey);
      if (storedDetails) {
        try {
          const parsedDetails = JSON.parse(storedDetails);
          
          // If there are wallet addresses saved, find the corresponding wallet
          if (parsedDetails.walletAddresses && parsedDetails.walletAddresses.length > 0) {
            const savedWalletAddress = parsedDetails.walletAddresses[0];
            // Find matching wallet
            const matchingWallet = wallets.find(w => w.address === savedWalletAddress);
            if (matchingWallet) {
              setSelectedWallet(matchingWallet);
              setWalletAddresses([savedWalletAddress]);
            }
          }
        } catch (e) {
          console.error('Error parsing saved agent strategy details:', e);
        }
      }
    } catch (error) {
      console.error('Failed to fetch agent details:', error);
      
      // Handle offline mode - check localStorage for agent status
      if (cachedStatus) {
        try {
          const parsedStatus = JSON.parse(cachedStatus);
          const isActive = parsedStatus.active;
          
          if (isActive) {
            setTerminalVisible(true);
            
            if (parsedStatus.strategyId) {
              setCurrentStrategy(parsedStatus.strategyId);
            }
            
            if (parsedStatus.isPaused) {
              setIsPaused(true);
            } else {
              setIsPaused(false);
            }
            
            console.log('Using cached agent status - agent is active');
          }
        } catch (e) {
          console.error('Error parsing cached agent status:', e);
        }
      }
    }
  };
  
  // Start a strategy
  const handleStrategyStart = async (strategy) => {
    try {
      if (!selectedAgent) {
        throw new Error('No agent selected');
      }
      
      setActionInProgress(true);
      
      // Show terminal
      setTerminalVisible(true);
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Starting strategy...`);
      
      // Start the strategy with the external agent API
      console.log('Starting strategy with external agent:', strategy);
      const result = await tradingAgentApi.startStrategy(selectedAgent.id, strategy);
      console.log('Start strategy response:', result);
      
      if (result && result.strategyId) {
        console.log(`Strategy started successfully with ID ${result.strategyId}`);
        setCurrentStrategy(result.strategyId);
        console.log(`Agent external ID: ${selectedAgent.external_id}`);
        
        // Save the strategy ID to localStorage
        localStorage.setItem(`active_strategy_${selectedAgent.id}`, result.strategyId);
        localStorage.setItem(`agent_status_${selectedAgent.id}`, JSON.stringify({
          active: true,
          strategyId: result.strategyId,
          externalId: selectedAgent.external_id
        }));
        
        // Start persistent WebSocket connection for background message saving - LOGS type
        if (selectedAgent.external_id) {
          console.log(`[Dashboard] Starting persistent WebSocket for LOGS for strategy ${result.strategyId}`);
          // Use 'logs' connection type for terminal logs
          globalWebSocketService.startConnection(selectedAgent.id, selectedAgent.external_id, result.strategyId, 'logs');
        }
        
        // Update agent status
        const updatedAgents = agents.map(agent => 
          agent.id === selectedAgent.id ? { ...agent, active: true } : agent
        );
        setAgents(updatedAgents);
        
        // Update selected agent
        setSelectedAgent(prev => ({ ...prev, active: true }));
        
        // Reset pause state
        setIsPaused(false);
        
        // Add success message to terminal
        addTerminalOutput(`[${new Date().toLocaleTimeString()}] Strategy started successfully: ${result.message || ''}`);
      }
      
      return result;
    } catch (error) {
      console.error('Failed to start strategy:', error);
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Error: ${error.response?.data?.error || error.message}`);
      throw error;
    } finally {
      setActionInProgress(false);
    }
  };
  
  // Stop a strategy
  const handleStrategyStop = async () => {
    if (!selectedAgent || !currentStrategy) return;
    
    try {
      setActionInProgress(true);
      
      // Add message to terminal
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Stopping strategy...`);
      
      // Stop the strategy using the external API
      console.log('Stopping strategy:', { agentId: selectedAgent.id, strategyId: currentStrategy, externalId: selectedAgent.external_id });
      const result = await tradingAgentApi.stopStrategy(selectedAgent.id, currentStrategy);
      console.log('Stop strategy response:', result);
      
      // Clear the stored strategy
      localStorage.removeItem(`active_strategy_${selectedAgent.id}`);
      localStorage.setItem(`agent_status_${selectedAgent.id}`, JSON.stringify({
        active: false,
        strategyId: null
      }));
      
      // Stop persistent WebSocket connection
      if (selectedAgent.external_id) {
        console.log(`[Dashboard] Stopping persistent WebSocket for external ID ${selectedAgent.external_id}`);
        globalWebSocketService.stopConnection(selectedAgent.id, selectedAgent.external_id, 'logs');
      }
      
      // Update agent status regardless of response
      const updatedAgents = agents.map(agent => 
        agent.id === selectedAgent.id ? { ...agent, active: false } : agent
      );
      setAgents(updatedAgents);
      
      // Update selected agent
      setSelectedAgent(prev => ({ ...prev, active: false }));
      
      // Clear the paused state
      setIsPaused(false);
      
      // Add success message
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Strategy stopped successfully`);
      return result;
    } catch (error) {
      console.error('Failed to stop strategy:', error);
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Error stopping strategy: ${error.response?.data?.error || error.message}`);
      throw error;
    } finally {
      setActionInProgress(false);
    }
  };
  
  // Pause a strategy
  const handleStrategyPause = async () => {
    if (!selectedAgent || !currentStrategy) return;
    
    try {
      setActionInProgress(true);
      
      // Add message to terminal
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Pausing strategy...`);
      // Pause the strategy using the external API
      console.log('Pausing strategy:', { agentId: selectedAgent.id, strategyId: currentStrategy, externalId: selectedAgent.external_id });
      
      try {
        const result = await tradingAgentApi.pauseStrategy(selectedAgent.id, currentStrategy);
        console.log('Pause strategy response:', result);
        
        // Update pause state
        setIsPaused(true);
        
        // Update localStorage with paused state
        const cachedStatus = localStorage.getItem(`agent_status_${selectedAgent.id}`);
        if (cachedStatus) {
          try {
            const parsedStatus = JSON.parse(cachedStatus);
            parsedStatus.isPaused = true;
            localStorage.setItem(`agent_status_${selectedAgent.id}`, JSON.stringify(parsedStatus));
          } catch (e) {
            console.error('Error updating cached agent status:', e);
          }
        }
        
        // Add success message
        addTerminalOutput(`[${new Date().toLocaleTimeString()}] Strategy paused successfully`);
        
        return result;
      } catch (apiError) {
        console.error('API error when pausing strategy:', apiError);
        
        // Check if the response status was 200 (success) despite the error
        // This handles cases where the backend returns success but frontend considers it an error
        if (apiError.response && apiError.response.status === 200) {
          console.log('Received 200 OK response despite error, treating as success');
          
          // Update pause state
          setIsPaused(true);
          
          // Update localStorage with paused state
          const cachedStatus = localStorage.getItem(`agent_status_${selectedAgent.id}`);
          if (cachedStatus) {
            try {
              const parsedStatus = JSON.parse(cachedStatus);
              parsedStatus.isPaused = true;
              localStorage.setItem(`agent_status_${selectedAgent.id}`, JSON.stringify(parsedStatus));
            } catch (e) {
              console.error('Error updating cached agent status:', e);
            }
          }
          
          // Add success message
          setTerminalOutput(prev => [
            ...prev, 
            `[${new Date().toLocaleTimeString()}] Strategy paused successfully`
          ]);
          
          return { success: true, paused: true };
        } else {
          throw apiError; // Re-throw if it's a real error
        }
      }
    } catch (error) {
      console.error('Failed to pause strategy:', error);
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Error pausing strategy: ${error.response?.data?.error || error.message}`);
      throw error;
    } finally {
      setActionInProgress(false);
    }
  };
  
  // Resume a strategy
  const handleStrategyResume = async () => {
    if (!selectedAgent || !currentStrategy) return;
    
    try {
      setActionInProgress(true);
      
      // Add message to terminal
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Resuming strategy...`);
      
      // Resume the strategy using the external API
      console.log('Resuming strategy:', { agentId: selectedAgent.id, strategyId: currentStrategy, externalId: selectedAgent.external_id });
      
      try {
        const result = await tradingAgentApi.resumeStrategy(selectedAgent.id, currentStrategy);
        console.log('Resume strategy response:', result);
        
        // Update pause state
        setIsPaused(false);
        
        // Update localStorage with resumed state
        const cachedStatus = localStorage.getItem(`agent_status_${selectedAgent.id}`);
        if (cachedStatus) {
          try {
            const parsedStatus = JSON.parse(cachedStatus);
            parsedStatus.isPaused = false;
            localStorage.setItem(`agent_status_${selectedAgent.id}`, JSON.stringify(parsedStatus));
          } catch (e) {
            console.error('Error updating cached agent status:', e);
          }
        }
        
        // Add success message
        addTerminalOutput(`[${new Date().toLocaleTimeString()}] Strategy resumed successfully`);
        
        return result;
      } catch (apiError) {
        console.error('API error when resuming strategy:', apiError);
        
        // Check if the response status was 200 (success) despite the error
        if (apiError.response && apiError.response.status === 200) {
          console.log('Received 200 OK response despite error, treating as success');
          
          // Update pause state
          setIsPaused(false);
          
          // Update localStorage with resumed state
          const cachedStatus = localStorage.getItem(`agent_status_${selectedAgent.id}`);
          if (cachedStatus) {
            try {
              const parsedStatus = JSON.parse(cachedStatus);
              parsedStatus.isPaused = false;
              localStorage.setItem(`agent_status_${selectedAgent.id}`, JSON.stringify(parsedStatus));
            } catch (e) {
              console.error('Error updating cached agent status:', e);
            }
          }
          
          // Add success message
          setTerminalOutput(prev => [
            ...prev, 
            `[${new Date().toLocaleTimeString()}] Strategy resumed successfully`
          ]);
          
          return { success: true, resumed: true };
        } else {
          throw apiError;
        }
      }
    } catch (error) {
      console.error('Failed to resume strategy:', error);
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Error resuming strategy: ${error.response?.data?.error || error.message}`);
      throw error;
    } finally {
      setActionInProgress(false);
    }
  };

  // Restart a strategy
  const handleRestartStrategy = async () => {
    if (!selectedAgent || !currentStrategy) return;
    
    try {
      setActionInProgress(true);
      
      // Add message to terminal
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Restarting strategy...`);
      
      // Get the strategy details from the terminal output or stored state
      // This is a simplification - you might need to store strategy details somewhere
      const result = await tradingAgentApi.startStrategy(selectedAgent.id, {
        // Include the basic strategy details
        external_id: currentStrategy
      });
      
      console.log('Restart strategy response:', result);
      
      // Save the strategy ID to localStorage
      if (result && result.strategyId) {
        localStorage.setItem(`active_strategy_${selectedAgent.id}`, result.strategyId);
        localStorage.setItem(`agent_status_${selectedAgent.id}`, JSON.stringify({
          active: true,
          strategyId: result.strategyId,
          externalId: selectedAgent.external_id,
          isPaused: false
        }));
      }
      
      // Update agent status
      const updatedAgents = agents.map(agent => 
        agent.id === selectedAgent.id ? { ...agent, active: true } : agent
      );
      setAgents(updatedAgents);
      
      // Update selected agent
      setSelectedAgent(prev => ({ ...prev, active: true }));
      
      // Reset pause state
      setIsPaused(false);
      
      // Add success message
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Strategy restarted successfully`);
    } catch (error) {
      console.error('Failed to restart strategy:', error);
      addTerminalOutput(`[${new Date().toLocaleTimeString()}] Error restarting strategy: ${error.response?.data?.error || error.message}`);
    } finally {
      setActionInProgress(false);
    }
  };

  // Delete confirmation modal - Responsive
  const DeleteConfirmationModal = () => {
    if (!deleteConfirmOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 dark:bg-black dark:bg-opacity-70 p-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 sm:p-6 shadow-xl max-w-sm sm:max-w-md w-full transition-colors">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100 mb-3 sm:mb-4">Delete Agent</h3>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6">
            Are you sure you want to delete {selectedAgent?.name}? This will stop any running strategies and remove the agent from your account.
          </p>
          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
            <button
              onClick={() => setDeleteConfirmOpen(false)}
              className="w-full sm:w-auto px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors text-sm sm:text-base"
            >
              Cancel
            </button>
            <button
              onClick={deleteAgent}
              disabled={actionInProgress}
              className="w-full sm:w-auto px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed text-sm sm:text-base"
            >
              {actionInProgress ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // State for mobile sidebar
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar when clicking outside on mobile
  const closeSidebar = () => {
    setSidebarOpen(false);
  };

  // Render the dashboard
  return (
    <div className="flex min-h-screen lg:h-screen relative">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Sidebar - Responsive */}
      <div className={`${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      } fixed inset-y-0 left-0 z-50 w-64 sm:w-72 lg:translate-x-0 lg:static lg:inset-0 transition-transform duration-300 ease-in-out lg:transition-none`}>
        <Sidebar
          agents={agents}
          selectedAgent={selectedAgent}
          onSelectAgent={selectAgent}
          onCreateAgent={createAgent}
          onClose={closeSidebar}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal />

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0 lg:overflow-hidden">
        {/* Dashboard content */}
        <div className="flex flex-col flex-1 lg:overflow-hidden">
          {/* Header */}
        <header className="flex flex-col sm:flex-row items-start sm:items-center justify-between px-3 sm:px-6 py-2 sm:py-4 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 transition-colors">
          <div className="flex items-center min-w-0 flex-1 w-full sm:w-auto">
            {/* Mobile hamburger menu */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-1.5 sm:p-2 mr-2 sm:mr-3 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="min-w-0 flex-1">
              <h1 className="text-sm sm:text-lg lg:text-xl font-bold text-gray-800 dark:text-gray-100 truncate">Trading Agent</h1>
              {selectedAgent && (
                <div className="mt-0.5 flex items-center gap-1">
                  <div className="flex items-center min-w-0 max-w-[150px] sm:max-w-[200px]">
                    <span className="text-[10px] sm:text-xs text-gray-600 dark:text-gray-400 mr-1 hidden xs:inline">Agent:</span>
                    <span className="font-medium text-purple-700 dark:text-purple-300 truncate text-xs sm:text-sm">{formatAgentName(selectedAgent.name)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={copyAgentId}
                      className="p-1.5 sm:p-1 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-all relative group"
                      title="Copy agent ID"
                    >
                      {copiedAgentId ? (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 dark:text-green-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path d="M8 3a1 1 0 011-1h2a1 1 0 110 2H9a1 1 0 01-1-1z" />
                          <path d="M6 3a2 2 0 00-2 2v11a2 2 0 002 2h8a2 2 0 002-2V5a2 2 0 00-2-2 3 3 0 01-3 3H9a3 3 0 01-3-3z" />
                        </svg>
                      )}
                      {copiedAgentId && (
                        <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                          Copied!
                        </span>
                      )}
                    </button>
                    <button
                      onClick={() => setDeleteConfirmOpen(true)}
                      className="p-1.5 sm:p-1 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-full transition-colors"
                      title="Delete agent"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

            {/* Wallet connection - Responsive */}
            <div className="flex items-center mt-2 sm:mt-0 sm:ml-4 flex-shrink-0 w-full sm:w-auto">
              <WalletConnectSection
                walletConnected={walletConnected}
                walletBalance={walletBalance}
                selectedWallet={selectedWallet}
                onConnect={connectWallet}
                loading={connecting}
              />
            </div>
            {/* <div className="flex items-center">
              {!walletConnected ? (
                <button 
                  onClick={() => connectWallet(selectedWallet)}
                  disabled={connecting || !selectedWallet}
                  className="px-5 py-2 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors shadow-sm font-medium flex items-center"
                >
                  {connecting ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connecting...
                    </>
                  ) : (
                    selectedWallet ? (
                      <>
                        Connect {selectedWallet.name || 'Wallet'}
                        <span className="ml-2 text-xs bg-purple-800 px-2 py-1 rounded-full">
                          {selectedWallet.address.substring(0, 4)}...{selectedWallet.address.substring(selectedWallet.address.length - 4)}
                        </span>
                      </>
                    ) : (
                      'Connect Wallet'
                    )
                  )}
                </button>
              ) : (
                <div className="flex items-center space-x-6">
                  {walletBalance && (
                    <>
                      <div className="flex items-center text-gray-700 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                        <span className="font-bold text-purple-700 mr-1">
                          {walletBalance.solBalance ? Number(walletBalance.solBalance).toFixed(2) : '0.00'}
                        </span> 
                        <span>SOL</span>
                      </div>
                      {walletBalance.tokens && walletBalance.tokens.map(token => (
                        <div key={token.symbol} className="flex items-center text-gray-700 bg-gray-50 px-4 py-2 rounded-lg border border-gray-200">
                          <span className="font-bold text-purple-700 mr-1">
                            {token.balance ? Number(token.balance).toFixed(2) : '0.00'}
                          </span> 
                          <span>{token.symbol}</span>
                        </div>
                      ))}
                    </>
                  )}
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                    <span className="text-sm text-green-700">
                      {selectedWallet ? (
                        <>
                          {selectedWallet.name || 'Wallet'} Connected
                          <span className="text-xs ml-1 text-gray-500">
                            ({selectedWallet.address.substring(0, 4)}...{selectedWallet.address.substring(selectedWallet.address.length - 4)})
                          </span>
                        </>
                      ) : (
                        'Connected'
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div> */}

          </header>
          
          {/* Content area */}
          <div className="flex flex-col flex-1 p-2 sm:p-4 lg:p-6 bg-gray-50 dark:bg-gray-900 transition-colors lg:overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-purple-500"></div>
              </div>
            ) : error ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-6 py-4 rounded-lg border border-red-200 dark:border-red-700 shadow-sm transition-colors">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 mr-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {error}
                  </div>
                </div>
              </div>
              ) : selectedAgent ? (
              <div className="flex flex-col flex-1 lg:h-full">
                {/* Responsive split view - stack vertically on mobile, side by side on desktop */}
                <div className="flex flex-col xl:flex-row gap-2 sm:gap-3 lg:gap-4 lg:h-full">
                  {/* Chat panel - always visible */}
                  <div
                    ref={agentChatRef}
                    className={`bg-white dark:bg-gray-800 rounded-lg shadow transition-colors ${
                      terminalVisible && !terminalCollapsed
                        ? 'xl:w-1/2 flex-1 xl:flex-none' // Half width when terminal is expanded on desktop
                        : terminalVisible && terminalCollapsed
                        ? 'w-full flex-1' // Full width when terminal is collapsed
                        : 'w-full flex-1' // Full width when no terminal
                    } ${terminalVisible && !terminalCollapsed ? 'xl:min-h-full' : ''}`}
                  >
                    <AgentChat
                      agent={selectedAgent}
                      onStrategyStart={handleStrategyStart}
                      onWalletSelect={handleWalletSelectFromChat}
                    />
                    <div ref={chatEndRef} />
                  </div>

                  {/* Terminal panel - conditionally visible, collapsible on mobile */}
                  {terminalVisible && (
                    <div className={`xl:w-1/2 flex-1 xl:flex-none transition-all duration-300 ${
                      terminalCollapsed
                        ? 'min-h-[40px] max-h-[40px] xl:min-h-full xl:max-h-none'
                        : 'xl:min-h-full'
                    }`}>
                      <div className="bg-gray-900 text-white rounded-lg shadow-lg h-full flex flex-col">
                        {/* Terminal Header with Collapse Toggle */}
                        <div className="flex items-center justify-between p-2 sm:p-3 border-b border-gray-700">
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-1">
                              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-red-500 rounded-full"></div>
                              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-yellow-500 rounded-full"></div>
                              <div className="w-2 h-2 sm:w-3 sm:h-3 bg-green-500 rounded-full"></div>
                            </div>
                            <span className="text-xs sm:text-sm font-medium text-gray-300">Terminal</span>
                          </div>

                          {/* Collapse button - only visible on smaller screens */}
                          <button
                            onClick={() => setTerminalCollapsed(!terminalCollapsed)}
                            className="xl:hidden p-1 hover:bg-gray-800 rounded text-gray-400 hover:text-white transition-colors"
                            title={terminalCollapsed ? "Expand terminal" : "Collapse terminal"}
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {terminalCollapsed ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                              )}
                            </svg>
                          </button>
                        </div>

                        {/* Terminal Content - conditionally hidden on collapse */}
                        {!terminalCollapsed && (
                          <>
                            <TerminalControls
                              isRunning={selectedAgent?.active}
                              isPaused={isPaused}
                              onStop={handleStrategyStop}
                              onStart={handleRestartStrategy}
                              onPause={handleStrategyPause}
                              onResume={handleStrategyResume}
                              strategyId={currentStrategy}
                              agentId={selectedAgent?.id}
                              isLoading={actionInProgress}
                            />
                            <div className="flex-1 overflow-hidden">
                              <AgentTerminal
                                agentId={selectedAgent.id}
                                strategyId={currentStrategy}
                              />
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center max-w-sm sm:max-w-md p-4 sm:p-6 lg:p-8 mx-2 sm:mx-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg transition-colors">
                  <div className="mb-3 sm:mb-6">
                    <svg xmlns="http://www.w3.org/2000/svg" className="mx-auto h-10 w-10 sm:h-16 sm:w-16 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-4 text-gray-800 dark:text-gray-100">Welcome to Trading Agent</h2>
                  <p className="mb-6 sm:mb-8 text-sm sm:text-base text-gray-600 dark:text-gray-400">Create a new agent or select an existing one to get started with automated trading.</p>
                  <button
                    onClick={createAgent}
                    className="w-full sm:w-auto px-6 py-3 text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors shadow-md font-medium flex items-center justify-center mx-auto text-sm sm:text-base"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 sm:h-5 sm:w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                    Create Agent
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;